import React, { useEffect } from 'react';
import { IconButton } from '@material-ui/core';
import { withStyles, type WithStyles, createStyles } from '@material-ui/core/styles';
import useMediaQuery from '@material-ui/core/useMediaQuery';
import CancelIcon from '@material-ui/icons/Cancel';
import { colors } from '@skyslope/mache';
import { MD_MEDIA_QUERY } from '../common/constants';
interface IProps extends WithStyles<typeof styles> {
  visible: boolean;
  zoom: number;
  centered?: boolean;
  children: any;
  closeTooltip: () => void;
  isGroup?: boolean;
}

// eslint-disable-next-line func-style -- This code was written prior to enforcing lint rules
const styles = () =>
  createStyles({
    tooltip: {
      display: 'flex',
      alignItems: 'center',
      position: 'absolute',
      fontFamily: 'proxima-nova',
      zIndex: 10,
      bottom: 'calc(100% + 11px)',
      backgroundColor: '#FDF7D0',
      border: '1px solid #CAD5E0',
      color: '#3F5B77',
      whiteSpace: 'nowrap',
      fontSize: '11px',
      borderRadius: '4px',
      boxShadow:
        'inset 0 -1.5px 0 0 #E1E7ED, 0 6px 10px 0 rgba(0,32,64,0.07), 0 1px 18px 0 rgba(0,32,64,0.06), 0 3px 5px -1px rgba(0,32,64,0.07)',
      fontWeight: 'bold',
    },
    arrow: {
      width: '0',
      height: '0',
      position: 'absolute',
      borderLeft: '5px solid transparent',
      borderRight: '5px solid transparent',
      borderTop: '10px solid #fdf7d0',
      top: 'calc(100% - 2px)',
    },

    arrowShadow: {
      width: '0',
      height: '0',
      position: 'absolute',
      borderLeft: '5px solid transparent',
      borderRight: '5px solid transparent',
      borderTop: '10px solid #E1E7ED',
      top: 'calc(100%)',
    },
    span: {
      marginRight: '2px',
      color: `${colors.red}`,
    },
    cancelIcon: {
      color: colors.grey[600],
      height: '80%',
      width: '80%',
    },
  });

// eslint-disable-next-line func-style -- This code was written prior to enforcing lint rules
const Tooltip = (props: IProps) => {
  const { classes, zoom, visible, centered, children, isGroup } = props;
  const screenWidthIsLessThanOrEqualToMdBreakpoint = useMediaQuery(MD_MEDIA_QUERY);
  const wrapperMarginLeft = centered ? 10 * zoom : 0;
  const fontSize = screenWidthIsLessThanOrEqualToMdBreakpoint ? '' : zoom * 11;
  // Verify if the tooltip is out of bound and move it to the left if needed
  useEffect(() => {
    // Don't run if the tooltip is not visible or useMediaQuery hasn't returned a value yet
    if (!visible || screenWidthIsLessThanOrEqualToMdBreakpoint == undefined) return;

    try {
      // Get element and viewport information
      const element = document.getElementById('Tooltip');
      if (!element) return;

      let elementRect = element.getBoundingClientRect();
      let viewportRightBound = window.innerWidth;
      let isElementOutOfBoundInX = elementRect.x + elementRect.width > viewportRightBound;

      if (isElementOutOfBoundInX) {
        // If the tooltip is out of bound, move the tooltip and to the left
        moveTooltipToLeft(
          screenWidthIsLessThanOrEqualToMdBreakpoint,
          element,
          elementRect,
          viewportRightBound,
          wrapperMarginLeft,
          isGroup
        );
      } else {
        // If the tooltip is not out of bound, set the default arrow position
        moveTooltipToDefaultPosition(element);

        // Verify a second time if the tooltip is out of bound
        // This is because useMediaQuery always returns false the first time it's called and then it can return true
        elementRect = element.getBoundingClientRect();
        viewportRightBound = window.innerWidth;
        isElementOutOfBoundInX = elementRect.x + elementRect.width > viewportRightBound;
        if (isElementOutOfBoundInX) {
          // If the tooltip is out of bound, move the tooltip and to the left
          moveTooltipToLeft(
            screenWidthIsLessThanOrEqualToMdBreakpoint,
            element,
            elementRect,
            viewportRightBound,
            wrapperMarginLeft,
            isGroup
          );
        }
      }
    } catch {
      // Ignore error
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [visible, screenWidthIsLessThanOrEqualToMdBreakpoint, zoom, fontSize]);

  // Don't render tooltip if is not visible or useMediaQuery hasn't returned a value yet
  const tooltip =
    visible && screenWidthIsLessThanOrEqualToMdBreakpoint != undefined ? (
      <div
        className={classes.tooltip}
        style={{
          left: '0px',
          fontSize: fontSize,
          padding: screenWidthIsLessThanOrEqualToMdBreakpoint
            ? '2px 4px 2px 8px'
            : `${zoom * 2}px ${zoom * 4}px ${zoom * 2}px ${zoom * 8}px `,
          marginLeft: `-${wrapperMarginLeft}px`,
        }}
        data-spec="tooltipString"
        id="Tooltip"
      >
        {children}
        <IconButton
          style={{
            marginLeft: screenWidthIsLessThanOrEqualToMdBreakpoint ? '10px' : `${zoom * 6}px`,
            width: screenWidthIsLessThanOrEqualToMdBreakpoint ? '16px' : `${zoom * 16}px`,
            height: screenWidthIsLessThanOrEqualToMdBreakpoint ? '16px' : `${zoom * 16}px`,
            padding: `0px`,
          }}
          onClick={() => props.closeTooltip()}
        >
          <CancelIcon className={classes.cancelIcon} />
        </IconButton>
        <div className={classes.arrowShadow} data-spec="ToolTipArrowShadow" />
        <div className={classes.arrow} data-spec="ToolTipArrow" />
      </div>
    ) : null;
  return tooltip;
};

function moveTooltipToLeft(
  screenWidthIsLessThanOrEqualToMdBreakpoint: boolean,
  element: HTMLElement,
  elementRect: DOMRect,
  viewportRightBound: number,
  wrapperMarginLeft: number,
  isGroup: boolean | undefined
) {
  // Calculate extra margin for group block
  const extraMarginForGroupBlock = screenWidthIsLessThanOrEqualToMdBreakpoint ? 0 : isGroup ? wrapperMarginLeft : 0;
  // Extra margin left to have more space on the right edge
  const extraMarginForRightEdge = screenWidthIsLessThanOrEqualToMdBreakpoint ? 0 : 10;

  const newLeft = Math.abs(
    Math.floor(
      viewportRightBound - elementRect.x - elementRect.width - extraMarginForRightEdge - extraMarginForGroupBlock
    )
  );
  // Move tooltip to the left
  element.style.left = `-${newLeft}px`;

  // Move arrow to the left
  let arrowElements = document.querySelectorAll<HTMLElement>('[data-spec*="ToolTipArrow"]');
  arrowElements.forEach((arrowElement) => {
    arrowElement.style.left = `calc(11% - 5px + ${newLeft}px)`;
  });
}

function moveTooltipToDefaultPosition(element: HTMLElement) {
  element.style.left = '0px';
  let arrowElements = document.querySelectorAll<HTMLElement>('[data-spec*="ToolTipArrow"]');
  arrowElements.forEach((arrowElement) => {
    arrowElement.style.left = `calc(11% - 5px)`;
  });
}

// eslint-disable-next-line import/no-default-export -- This code was written prior to enforcing lint rules
export default withStyles(styles)(Tooltip);
